import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { LoadingSpinner, PageError } from '@/components/ui';
import type { DoneTask, TasksHistoryDay } from '@haccp/api-client';

export default function TasksDonePage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();

  const { data: history = [], isLoading, error } = useQuery({
    queryKey: ['tasks-history'],
    queryFn: () => apiClient.getTasksHistory(30),
  });

  const totalTasks = history.reduce((sum: number, day: TasksHistoryDay) => sum + day.tasks.length, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              <span className="text-2xl">✅</span>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('tasksDone.title')}</h1>
              <p className="text-gray-600 mt-1">{t('tasksDone.taskCount', { count: totalTasks })}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <LoadingSpinner message={t('tasksDone.loading')} />
        ) : error ? (
          <PageError message={t('tasksDone.error')} />
        ) : history.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center">
            <span className="text-5xl block mb-4">📭</span>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('tasksDone.noHistory')}</h2>
            <p className="text-gray-500">{t('tasksDone.noHistoryDesc')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {history.map((day: TasksHistoryDay) => (
              <DaySection key={day.date} day={day} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DaySection({ day }: { day: TasksHistoryDay }) {
  const { t, i18n } = useTranslation();
  const dateObj = new Date(day.date + 'T00:00:00');
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const isToday = day.date === today.toISOString().slice(0, 10);
  const isYesterday = day.date === yesterday.toISOString().slice(0, 10);

  const dateLabel = isToday
    ? t('tasksDone.today')
    : isYesterday
      ? t('tasksDone.yesterday')
      : dateObj.toLocaleDateString(i18n.language, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        });

  const byType: Record<string, DoneTask[]> = {};
  for (const task of day.tasks) {
    if (!byType[task.type]) byType[task.type] = [];
    byType[task.type].push(task);
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-lg font-semibold text-gray-900 capitalize">{dateLabel}</h2>
        <span className="text-sm text-gray-400 font-medium">{t('tasksDone.dayTaskCount', { count: day.tasks.length })}</span>
      </div>
      <div className="bg-white rounded-xl shadow overflow-hidden divide-y divide-gray-100">
        {day.tasks.map((task: DoneTask) => (
          <DoneTaskRow key={task.id} task={task} />
        ))}
      </div>
    </div>
  );
}

function DoneTaskRow({ task }: { task: DoneTask }) {
  const { t, i18n } = useTranslation();

  const TYPE_CONFIG = {
    TEMPERATURE: { icon: '🌡️', label: t('tasksDone.typeTemp') },
    CLEANING: { icon: '🧹', label: t('tasksDone.typeCleaning') },
    OIL: { icon: '🍟', label: t('tasksDone.typeOil') },
  } as const;

  const config = TYPE_CONFIG[task.type as keyof typeof TYPE_CONFIG];
  const time = new Date(task.doneAt).toLocaleTimeString(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="flex items-center gap-3 p-4">
      <span className="text-lg">{config?.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">{task.title}</span>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
            {config?.label}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-0.5">{task.subtitle}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-medium text-gray-700">{time}</p>
        {task.doneBy && (
          <p className="text-xs text-gray-400">{task.doneBy}</p>
        )}
      </div>
    </div>
  );
}
